import { cloneElement, CSSProperties, FC, isValidElement, ReactElement, ReactNode, useMemo, useState } from 'react';
import { Avatar, Button, Col, Row } from 'antd';
import { TbChevronRight, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownPanel from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import EmptySection from '@/shared/components/layouts/views/EmptySection';

export interface SearchFilterFieldPickerItem {
    group: string;
    icon: ReactNode;
    id: string;
    label: string;
}

interface SearchFilterFieldPickerProps {
    groupLabel?: 'groups' | 'properties';
    items: SearchFilterFieldPickerItem[];
    onSelect: (id: string) => void;
    selectedId?: string;
    width?: string | number;
}

const normalizePickerIcon = (icon: ReactNode, color: string) => {
    if (!isValidElement(icon)) {
        return icon;
    }

    const element = icon as ReactElement<{ size?: number | string; style?: CSSProperties }>;

    return cloneElement(element, {
        size: '0.75rem',
        style: {
            color,
            display: 'block',
            flex: 'none',
            margin: 0,
            verticalAlign: 'middle',
        },
    });
};

/**
 * Shared field-picking surface used both when adding a Search filter and when
 * changing an existing filter's field. Keeping both entry points here avoids
 * subtle drift in search, grouping, icon sizing, spacing, and empty states.
 */
const SearchFilterFieldPicker: FC<SearchFilterFieldPickerProps> = ({
    groupLabel = 'groups',
    items,
    onSelect,
    selectedId,
    width = '28rem',
}) => {
    const { token } = useAntDesignTheme();
    const [search, setSearch] = useState('');
    const filteredItems = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();

        if (!normalizedSearch) {
            return items;
        }

        return items.filter((item) =>
            [item.group, item.id, item.label].some((value) => value.toLocaleLowerCase().includes(normalizedSearch)),
        );
    }, [items, search]);
    const groups = useMemo(() => Array.from(new Set(filteredItems.map((item) => item.group))), [filteredItems]);

    return (
        <DropdownPanel
            contentProps={{ onClick: (event) => event.stopPropagation() }}
            contentStyle={{ padding: '0 0.75rem 0.75rem' }}
            footer={<LearnMoreFooter topic='search filters' variant='dropdown' />}
            maxHeight='32rem'
            search={{
                ariaLabel: 'Search filter fields',
                bordered: true,
                placeholder: 'Search Properties...',
                setValue: setSearch,
                size: 'small',
                value: search,
            }}
            width={width}
        >
            {groups.map((group) => (
                <div key={group} style={{ marginTop: '0.5rem' }}>
                    <small
                        style={{
                            color: token.colorTextTertiary,
                            display: 'block',
                            marginBottom: '0.375rem',
                        }}
                    >
                        {groupLabel === 'properties'
                            ? group === 'Advanced Filters'
                                ? 'Advanced'
                                : 'Properties'
                            : group}
                    </small>
                    <div style={{ display: 'grid', gap: '0.125rem' }}>
                        {filteredItems
                            .filter((item) => item.group === group)
                            .map((item) => (
                                <Button
                                    block
                                    key={item.id}
                                    onClick={() => onSelect(item.id)}
                                    style={{
                                        background: item.id === selectedId ? token.controlItemBgActive : undefined,
                                        height: 28,
                                        textAlign: 'left',
                                    }}
                                    type='text'
                                >
                                    <Row align='middle' style={{ height: '100%', width: '100%' }} wrap={false}>
                                        <Col flex='none'>
                                            <Avatar
                                                icon={
                                                    <span
                                                        style={{
                                                            alignItems: 'center',
                                                            display: 'inline-flex',
                                                            height: '100%',
                                                            justifyContent: 'center',
                                                            lineHeight: 1,
                                                            width: '100%',
                                                        }}
                                                    >
                                                        {normalizePickerIcon(item.icon, token.colorTextTertiary)}
                                                    </span>
                                                }
                                                shape='square'
                                                size={18}
                                                style={{
                                                    background: token.colorBgContainer,
                                                    border: `1px solid ${token.colorBorder}`,
                                                }}
                                            />
                                        </Col>
                                        <Col flex='auto' style={{ marginLeft: '0.375rem', minWidth: 0 }}>
                                            <div
                                                style={{
                                                    color: token.colorText,
                                                    fontWeight: 500,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {item.label}
                                            </div>
                                        </Col>
                                        <Col flex='none'>
                                            <TbChevronRight
                                                size='1rem'
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    display: 'block',
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </Button>
                            ))}
                    </div>
                </div>
            ))}
            {filteredItems.length === 0 ? (
                <EmptySection
                    description='Try another search term.'
                    height='10rem'
                    icon={<TbSearch size='75%' style={{ color: token.colorTextTertiary }} />}
                    label='No Matching Properties'
                />
            ) : null}
        </DropdownPanel>
    );
};

export default SearchFilterFieldPicker;
